(() => {
  const initialized = new WeakSet();
  const confirmedEntriesStorageKey = 'vacynte:prelude-confirmed-email-hashes:v2';
  const pendingEntryHashKey = 'vacynte:prelude-pending-email-hash:v2';
  const pendingEntryEmailKey = 'vacynte:prelude-pending-email:v2';

  function normalizeEmail(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getConfirmedEntries() {
    try {
      const stored = JSON.parse(window.localStorage.getItem(confirmedEntriesStorageKey) || '[]');
      return Array.isArray(stored) ? stored.filter((entry) => typeof entry === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  function hasConfirmedEntry(emailHash) {
    return Boolean(emailHash && getConfirmedEntries().includes(emailHash));
  }

  function recordConfirmedEntry(emailHash) {
    if (!emailHash) return;

    try {
      const entries = getConfirmedEntries();
      if (!entries.includes(emailHash)) entries.push(emailHash);
      window.localStorage.setItem(confirmedEntriesStorageKey, JSON.stringify(entries.slice(-20)));
    } catch (error) {
      // The signup still works when storage is blocked by browser privacy settings.
    }
  }

  function readSessionValue(key) {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  }

  function writePendingEntry(emailHash, email) {
    try {
      if (emailHash) window.sessionStorage.setItem(pendingEntryHashKey, emailHash);
      window.sessionStorage.setItem(pendingEntryEmailKey, email);
    } catch (error) {
      // The form can submit without session storage.
    }
  }

  function clearPendingEntry({ keepEmail = false } = {}) {
    try {
      window.sessionStorage.removeItem(pendingEntryHashKey);
      if (!keepEmail) window.sessionStorage.removeItem(pendingEntryEmailKey);
    } catch (error) {
      // Nothing else is required when storage is unavailable.
    }
  }

  async function hashEmail(email) {
    if (!window.crypto?.subtle || typeof window.TextEncoder !== 'function') return '';

    const data = new window.TextEncoder().encode(normalizeEmail(email));
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  function nativeSubmit(form) {
    window.HTMLFormElement.prototype.submit.call(form);
  }

  function initialize(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mark = root.querySelector('[data-vacynte-mark-stage]');
    let spinTimer;
    let resetTimer;

    if (mark && !reduceMotion) {
      const scheduleSpin = (delay) => {
        spinTimer = window.setTimeout(() => {
          mark.classList.add('is-spinning');
          resetTimer = window.setTimeout(() => {
            mark.classList.remove('is-spinning');
            scheduleSpin(9000 + Math.random() * 8000);
          }, 4400);
        }, delay);
      };

      scheduleSpin(1800 + Math.random() * 1000);

      root.addEventListener('vacynte:destroy', () => {
        window.clearTimeout(spinTimer);
        window.clearTimeout(resetTimer);
      }, { once: true });
    }

    root.querySelectorAll('[data-vacynte-video] video').forEach((video) => {
      video.muted = true;
      video.playsInline = true;

      if (reduceMotion) {
        video.pause();
      } else {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
    });

    const form = root.querySelector('.vacynte-access-form');
    const emailInput = root.querySelector('[data-vacynte-email]');
    const submit = root.querySelector('[data-vacynte-submit]');
    const submitLabel = root.querySelector('[data-vacynte-submit-label]');
    const formControls = root.querySelector('[data-vacynte-form-controls]');
    const success = root.querySelector('[data-vacynte-success]');
    const successClose = root.querySelector('[data-vacynte-success-close]');
    const confirmedEmailField = root.querySelector('[data-vacynte-confirmed-email]');
    const already = root.querySelector('[data-vacynte-already]');
    const alreadyDetail = root.querySelector('[data-vacynte-already-detail]');
    const serverError = root.querySelector('[data-vacynte-server-error]');
    const traceOpen = root.querySelector('[data-vacynte-trace-open]');
    const tracePanel = root.querySelector('[data-vacynte-trace-panel]');
    const traceClose = root.querySelector('[data-vacynte-trace-close]');
    const traceForm = root.querySelector('.vacynte-trace-response-form');
    const traceEmail = root.querySelector('[data-vacynte-trace-email]');
    const traceResult = root.querySelector('[data-vacynte-trace-result]');
    const traceSubmit = root.querySelector('[data-vacynte-trace-submit]');
    const traceSubmitLabel = root.querySelector('[data-vacynte-trace-submit-label]');

    const resetSubmit = () => {
      if (!submit || !submitLabel) return;
      submit.disabled = false;
      submit.classList.remove('is-loading');
      submitLabel.textContent = submit.dataset.defaultLabel || 'ENTER THE PRELUDE';
    };

    const showAlreadyEntered = ({ duplicate = false, focus = false } = {}) => {
      if (!already) return;

      if (success) success.hidden = true;
      if (tracePanel) tracePanel.hidden = true;
      if (formControls) formControls.hidden = true;
      if (alreadyDetail) alreadyDetail.hidden = !duplicate;
      already.hidden = false;
      already.dataset.duplicate = duplicate ? 'true' : 'false';
      form?.classList.add('is-already-entered');

      if (focus) {
        window.requestAnimationFrame(() => already.focus({ preventScroll: true }));
      }
    };

    const resolveTraceEmail = () => normalizeEmail(
      confirmedEmailField?.value || readSessionValue(pendingEntryEmailKey)
    );

    const showTracePanel = ({ focus = true } = {}) => {
      if (!tracePanel) return;

      if (success) success.hidden = true;
      if (formControls) formControls.hidden = true;
      if (already) already.hidden = true;
      tracePanel.hidden = false;

      if (traceEmail && !traceEmail.value) traceEmail.value = resolveTraceEmail();
      if (focus) {
        window.requestAnimationFrame(() => tracePanel.focus({ preventScroll: true }));
      }
    };

    if (success) {
      const confirmedEmail = resolveTraceEmail();
      const pendingHash = readSessionValue(pendingEntryHashKey);

      if (pendingHash) {
        recordConfirmedEntry(pendingHash);
        clearPendingEntry({ keepEmail: true });
      } else if (confirmedEmail) {
        hashEmail(confirmedEmail).then(recordConfirmedEntry).catch(() => {});
      }

      if (traceEmail) traceEmail.value = confirmedEmail;
      successClose?.addEventListener('click', () => {
        clearPendingEntry();
        showAlreadyEntered({ focus: true });
      });
      traceOpen?.addEventListener('click', () => showTracePanel());
    }

    traceClose?.addEventListener('click', () => {
      clearPendingEntry();
      showAlreadyEntered({ focus: true });
    });

    if (traceResult) {
      showTracePanel({ focus: false });
      if (traceResult.hasAttribute('data-vacynte-trace-sent')) clearPendingEntry();
      window.requestAnimationFrame(() => tracePanel?.focus({ preventScroll: true }));
    }

    if (serverError && /already|taken|subscribed/i.test(serverError.textContent || '')) {
      clearPendingEntry();
      showAlreadyEntered({ duplicate: true, focus: true });
    }

    if (form && emailInput && submit && submitLabel) {
      form.addEventListener('submit', async (event) => {
        if (!form.checkValidity()) return;
        event.preventDefault();

        const email = normalizeEmail(emailInput.value);
        submit.disabled = true;
        submit.classList.add('is-loading');
        submitLabel.textContent = 'ENTERING';

        try {
          const emailHash = await hashEmail(email);
          if (hasConfirmedEntry(emailHash)) {
            resetSubmit();
            showAlreadyEntered({ duplicate: true, focus: true });
            return;
          }

          writePendingEntry(emailHash, email);
          nativeSubmit(form);
        } catch (error) {
          writePendingEntry('', email);
          nativeSubmit(form);
        }
      });
    }

    if (traceForm && traceSubmit && traceSubmitLabel) {
      traceForm.addEventListener('submit', () => {
        if (traceEmail && !traceEmail.value) traceEmail.value = resolveTraceEmail();
        if (!traceForm.checkValidity()) return;
        traceSubmit.disabled = true;
        traceSubmit.classList.add('is-loading');
        traceSubmitLabel.textContent = 'LEAVING A TRACE';
      });
    }

    if (success) {
      const settleSuccessView = () => {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        window.scrollTo(0, 0);
        success.focus({ preventScroll: true });
      };

      if (window.location.hash || window.location.search.includes('customer_posted=true')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('customer_posted');
        url.hash = '';
        window.history.replaceState(
          window.history.state,
          '',
          url.pathname + url.search
        );
      }

      settleSuccessView();
      window.requestAnimationFrame(() => {
        settleSuccessView();
        window.requestAnimationFrame(settleSuccessView);
      });
      window.setTimeout(settleSuccessView, 300);
    }
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-vacynte-waitlist]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-vacynte-waitlist]');
    if (root) root.dispatchEvent(new Event('vacynte:destroy'));
  });
})();
